import { Injectable, inject } from '@angular/core'
import {
  Filter,
  HttpParamsBuilder,
  KafentEventService,
  RestClientPaged,
} from '@elderbyte/ngx-starter'
import { WorkItem, WorkItemEvent, WorkItemUpdateOne } from './work-item'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { HttpClient } from '@angular/common/http'
import { environment } from '../../../../environments/environment'
import { Observable } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { WorkItemTransfer } from './work-item-transfer'
import { ProcessPlan } from '../../process/process-plans/process-plan'
import { WorkItemCommand, WorkItemCommandResult } from './commands/work-item-command'
import {
  Constraint,
  toQueryParam,
} from '../../shopfloor/workcenters/capability/constraints/constraint'

export class WorkItemFilter {
  /***************************************************************************
   *                                                                         *
   * Constants                                                               *
   *                                                                         *
   **************************************************************************/

  public static readonly IDS_KEY = 'ids'

  public static readonly NUMBERS_KEY = 'numbers'

  public static readonly WORK_ORDER_IDS_KEY = 'workOrderIds'

  public static readonly WORK_BATCH_ID_KEY = 'workBatchId'

  public static readonly WORK_CENTER_IDS_KEY = 'workCenterIds'

  public static readonly PROCESS_PLAN_IDS_KEY = 'processPlanIds'

  public static readonly PROCESSING_STATES_KEY = 'processingStates'

  public static readonly PACKAGING_STATES = 'packagingStates'

  public static readonly ITEM_CONSTRAINTS_KEY = 'constraints'

  /***************************************************************************
   *                                                                         *
   * Static Builders                                                         *
   *                                                                         *
   **************************************************************************/

  public static ids(workItemIds: string[] | string): Filter {
    return new Filter(this.IDS_KEY, workItemIds)
  }

  public static numbers(numbers: string[] | string): Filter {
    return new Filter(this.NUMBERS_KEY, numbers)
  }

  public static workOrderId(workOrderId: string): Filter {
    return new Filter(this.WORK_ORDER_IDS_KEY, workOrderId)
  }

  public static workBatchId(workBatchId: string): Filter {
    return new Filter(this.WORK_BATCH_ID_KEY, workBatchId)
  }

  public static workCenterId(workCenterId: string): Filter {
    return new Filter(this.WORK_CENTER_IDS_KEY, workCenterId)
  }

  public static processPlans(processPlans: ProcessPlan[]): Filter {
    return WorkItemFilter.processPlanIds(processPlans.map((pp) => pp.id))
  }

  public static processPlanIds(processPlanIds: string[] | string): Filter {
    return new Filter(this.PROCESS_PLAN_IDS_KEY, processPlanIds)
  }

  public static processingStates(states: string[]): Filter {
    return new Filter(this.PROCESSING_STATES_KEY, states)
  }

  public static packagingStates(packagingStates: string[]): Filter {
    return new Filter(this.PACKAGING_STATES, packagingStates)
  }

  public static constraints(constraints: Constraint[]): Filter {
    return new Filter(
      this.ITEM_CONSTRAINTS_KEY,
      constraints
        .filter((constraint) => constraint?.values?.length > 0)
        .map((constraint) => toQueryParam(constraint))
    )
  }
}

@Injectable({
  providedIn: 'root',
})
export class WorkItemService extends RestClientPaged<WorkItem, string> {
  private kafentService = inject(KafentEventService)

  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const http = inject(HttpClient)

    super(environment.apiPath + '/work-items', http, {
      idField: 'id',
    })
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public patch(patch: WorkItemUpdateOne): Observable<WorkItem> {
    return this.patchInternal(patch.id, patch)
  }

  public itemChanged(orderId: string): Observable<WorkItemEvent> {
    return this.kafentService
      .getLiveEventsWith({
        topic: environment.kafentWorkItemEvents,
      })
      .pipe(
        map((e) => new WorkItemEvent(e.key, e.value)),
        filter((e) => {
          if (e.item) {
            return e.item.workOrderId === orderId
          }
          return true
        })
      )
  }

  public imageUrl(item: WorkItem): string {
    return item.imageUrl
  }

  public transfer(transfer: WorkItemTransfer): Observable<WorkItem[]> {
    return this.http.put<WorkItem[]>(this.restEndpoint + '/transfer', transfer)
  }

  public emit(itemFilter: Filter): Observable<any> {
    return this.http.post<any>(this.restEndpoint + '/events', null, {
      params: HttpParamsBuilder.start().appendFilter(itemFilter).build(),
    })
  }

  /***************************************************************************
   *                                                                         *
   * Commands                                                                *
   *                                                                         *
   **************************************************************************/

  public executeCommand(cmd: WorkItemCommand): Observable<WorkItemCommandResult> {
    return this.http.post<WorkItemCommandResult>(this.getEntityUrl(cmd.itemId) + '/commands', cmd)
  }
}
